import { Link, useParams } from 'react-router-dom';
import Seo, { SITE_NAME, SITE_URL } from '@/components/feature/Seo';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SceneCard from '@/components/feature/SceneCard';
import { scenes, getSceneById } from '@/mocks/scenes';
import { rankingList } from '@/mocks/ranking';
import { formatDate, formatHeat } from '@/utils/format';
import ShareActions from '@/pages/scene/components/ShareActions';
import SceneComments from '@/pages/scene/components/SceneComments';

function SceneNotFound() {
  return (
    <>
      <Seo
        title={`内容未找到 | ${SITE_NAME}`}
        description="你想看的这段玩梗现场可能已经调整位置了，回到话题分类继续逛逛吧。"
        keywords="糖心吃瓜,名场面,玩梗"
        path="/topics"
      />
      <Navbar />
      <main className="flex min-h-[70vh] items-center justify-center px-4 py-28">
        <div className="max-w-md text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-500">
            <i className="ri-emotion-unhappy-line" aria-hidden="true" />
          </span>
          <h1 className="mt-5 text-2xl text-foreground-950">这段现场没找到</h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            可能链接不太对，或者这条内容被整理进了别的分类。去话题分类页看看今天最热的那几段吧。
          </p>
          <Link
            to="/topics"
            className="mt-6 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            返回话题分类
          </Link>
        </div>
      </main>
      <Footer />
    </>
  );
}

export default function SceneDetail() {
  const { id } = useParams();
  const scene = id ? getSceneById(id) : undefined;

  if (!scene) {
    return <SceneNotFound />;
  }

  const related = scenes
    .filter((item) => item.id !== scene.id && item.category === scene.category)
    .concat(scenes.filter((item) => item.id !== scene.id && item.category !== scene.category))
    .slice(0, 3);

  const topRanking = [...rankingList]
    .sort((a, b) => b.today - a.today)
    .filter((item) => item.id !== scene.id)
    .slice(0, 5);

  const sceneJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: scene.title,
      description: scene.excerpt,
      image: scene.cover,
      articleSection: scene.categoryName,
      keywords: scene.tags.join(','),
      datePublished: scene.date,
      dateModified: scene.date,
      inLanguage: 'zh-CN',
      author: {
        '@type': 'Organization',
        name: scene.author,
      },
      publisher: {
        '@type': 'Organization',
        name: SITE_NAME,
        url: SITE_URL,
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/scene/${scene.id}`,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: '话题分类', item: `${SITE_URL}/topics` },
        { '@type': 'ListItem', position: 3, name: scene.title, item: `${SITE_URL}/scene/${scene.id}` },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${scene.title} | ${SITE_NAME} ${scene.categoryName}`}
        description={scene.excerpt}
        keywords={`${scene.tags.join(',')},${scene.categoryName},糖心吃瓜,玩梗`}
        path={`/scene/${scene.id}`}
        jsonLd={sceneJsonLd}
      />

      <Navbar />

      <main className="pb-16 pt-24 md:pt-28">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '话题分类', to: '/topics' },
              { label: scene.categoryName, to: `/topics?category=${scene.category}` },
              { label: scene.title },
            ]}
          />

          <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_320px]">
            <article>
              <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-900">
                <i className="ri-price-tag-3-line" aria-hidden="true" />
                {scene.categoryName}
              </span>

              <h1 className="mt-4 text-2xl leading-snug text-foreground-950 md:text-4xl">
                {scene.title}
              </h1>

              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 md:text-sm">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-3-line" aria-hidden="true" />
                  {scene.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" aria-hidden="true" />
                  {formatDate(scene.date)}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line" aria-hidden="true" />
                  {scene.readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-fire-line text-primary-500" aria-hidden="true" />
                  热度 {formatHeat(scene.heat)}
                </span>
              </div>

              <div className="mt-6 overflow-hidden rounded-lg border border-background-200">
                <img
                  src={scene.cover}
                  alt={`${scene.title} 现场配图`}
                  title={`${scene.title} 玩梗名场面配图`}
                  className="h-56 w-full object-top md:h-80"
                />
              </div>

              <div className="mt-6">
                <ShareActions title={scene.title} />
              </div>

              <p className="mt-8 rounded-lg border-l-4 border-primary-500 bg-background-100 p-4 text-sm leading-relaxed text-foreground-800 md:p-5 md:text-base">
                {scene.excerpt}
              </p>

              <section className="mt-10">
                <h2 className="text-xl text-foreground-950 md:text-3xl">现场回顾</h2>
                <div className="mt-5 space-y-4">
                  {scene.content.map((paragraph, index) => (
                    <p
                      key={index}
                      className="text-sm leading-loose text-foreground-700 md:text-base"
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </section>

              <section className="mt-10 rounded-lg bg-secondary-950 p-5 text-background-100 md:p-7">
                <h2 className="m-0 text-xl text-background-50 md:text-2xl">高能台词回放</h2>
                <p className="mt-2 text-sm text-background-300">
                  这几句是现场最有记忆点的部分，也是被网友反复引用的来源。
                </p>
                <ul className="mt-5 space-y-4">
                  {scene.highlights.map((line) => (
                    <li key={line.label} className="border-l-2 border-accent-500 pl-4">
                      <p className="text-xs text-accent-300">{line.label}</p>
                      <p className="mt-1.5 text-sm leading-relaxed text-background-100 md:text-base">
                        {line.text}
                      </p>
                    </li>
                  ))}
                </ul>
              </section>

              <div className="mt-8 flex flex-wrap items-center gap-2">
                {scene.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/topics?q=${encodeURIComponent(tag)}`}
                    className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <SceneComments comments={scene.hotComments} />
            </article>

            <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="m-0 text-base text-foreground-950">内容信息</h2>
                <dl className="mt-4 space-y-3 text-sm">
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-foreground-500">所属分类</dt>
                    <dd className="text-foreground-900">{scene.categoryName}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-foreground-500">评论数</dt>
                    <dd className="text-foreground-900">{formatHeat(scene.comments)}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-foreground-500">弹幕数</dt>
                    <dd className="text-foreground-900">{formatHeat(scene.danmaku)}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-foreground-500">整理时间</dt>
                    <dd className="text-foreground-900">{formatDate(scene.date)}</dd>
                  </div>
                </dl>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="m-0 flex items-center gap-2 text-base text-foreground-950">
                  <i className="ri-links-line text-primary-500" aria-hidden="true" />
                  相关推荐
                </h2>
                <ul className="mt-4 space-y-4">
                  {related.map((item) => (
                    <li key={item.id}>
                      <Link to={`/scene/${item.id}`} className="group block">
                        <span className="line-clamp-2 text-sm text-foreground-900 transition-colors group-hover:text-primary-600">
                          {item.title}
                        </span>
                        <span className="mt-1 block text-xs text-foreground-500">
                          {item.categoryName} · 热度 {formatHeat(item.heat)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="m-0 flex items-center gap-2 text-base text-foreground-950">
                  <i className="ri-fire-line text-primary-500" aria-hidden="true" />
                  今日热力榜
                </h2>
                <ol className="mt-4 space-y-3">
                  {topRanking.map((item, index) => (
                    <li key={item.id}>
                      <Link
                        to={`/scene/${item.id}`}
                        className="flex items-start gap-3 text-sm text-foreground-800 transition-colors hover:text-primary-600"
                      >
                        <span
                          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-xs ${
                            index < 3
                              ? 'bg-accent-500 text-secondary-950'
                              : 'bg-background-200 text-foreground-600'
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="line-clamp-2">{item.title}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ol>
                <Link
                  to="/topics"
                  className="mt-5 flex items-center justify-center gap-1 whitespace-nowrap rounded-md bg-background-200 px-3 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-background-300"
                >
                  查看完整榜单
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </div>
            </aside>
          </div>

          <section className="mt-14">
            <div className="flex items-end justify-between gap-4 border-b border-background-200 pb-4">
              <h2 className="m-0 text-xl text-foreground-950 md:text-3xl">继续围观这几段</h2>
              <Link
                to="/topics"
                className="shrink-0 whitespace-nowrap text-sm text-primary-600 transition-colors hover:text-primary-700"
              >
                更多名场面
              </Link>
            </div>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <SceneCard key={`card-${item.id}`} scene={item} />
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}